import { ChangeEvent, FC, useCallback, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { toast } from "react-toastify"

import { DropdownMenu } from "components/inputs/DropdownMenu"
import { ModalNotesAction } from "components/modals/ModalNotesAction"
import { TablePagination } from "components/paginations/TablePagination"
import { buildReviewColumns } from "components/risk/columns"
import { REVIEW_PRIORITY_OPTIONS, REVIEW_STATUS_OPTIONS } from "components/risk/constants"
import { DataTable } from "components/tables/DataTable"

import { riskService } from "services/risk/risk.service"
import { ReviewDecision, ReviewItem, ReviewQueueFilters } from "services/risk/types"

export const ReviewQueueTab: FC = () => {
  const navigate = useNavigate()
  const [items, setItems] = useState<ReviewItem[]>([])
  const [loading, setLoading] = useState(true)
  const [_total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [resolvingId, setResolvingId] = useState<string | null>(null)
  const [resolutionModalId, setResolutionModalId] = useState<string | null>(null)

  const [filters, setFilters] = useState<ReviewQueueFilters>({
    page: 1,
    pageSize: 20,
    status: "PENDING"
  })

  const updateFilter = <K extends keyof ReviewQueueFilters>(field: K, value: ReviewQueueFilters[K]) =>
    setFilters((f) => ({ ...f, page: 1, [field]: value }))

  const loadItems = useCallback(async () => {
    setLoading(true)
    try {
      const res = await riskService.getReviewQueue(filters)
      if (res) {
        setItems(res.data)
        setTotal(res.total)
        setTotalPages(res.total_pages)
      }
    } finally {
      setLoading(false)
    }
  }, [filters])

  useEffect(() => {
    loadItems()
  }, [loadItems])

  const handleResolve = async (id: string, decision: ReviewDecision) => {
    if (decision === "REVIEWED") {
      setResolutionModalId(id)
      return
    }
    setResolvingId(id)
    try {
      await riskService.resolveReviewItem(id, decision)
      toast.success("Item dismissed")
      await loadItems()
    } finally {
      setResolvingId(null)
    }
  }

  const handleConfirmResolve = async (_data: any, notes: string) => {
    if (!resolutionModalId) return
    setResolvingId(resolutionModalId)
    try {
      await riskService.resolveReviewItem(resolutionModalId, "REVIEWED", notes || undefined)
      toast.success("Item marked as reviewed")
      setResolutionModalId(null)
      await loadItems()
    } finally {
      setResolvingId(null)
    }
  }

  const columns = buildReviewColumns({ navigate, resolvingId, onResolve: handleResolve })

  if (loading && items.length === 0) {
    return (
      <div className="flex justify-center p-8">
        <span className="loading loading-spinner loading-lg" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Review Queue</h3>
        <div className="flex gap-2">
          <div className="w-40">
            <DropdownMenu
              placeholder="All Statuses"
              options={REVIEW_STATUS_OPTIONS}
              value={filters.status || ""}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => updateFilter("status", e.target.value || undefined)}
            />
          </div>
          <div className="w-40">
            <DropdownMenu
              placeholder="All Priorities"
              options={REVIEW_PRIORITY_OPTIONS}
              value={filters.priority || ""}
              onChange={(e: ChangeEvent<HTMLSelectElement>) => updateFilter("priority", e.target.value || undefined)}
            />
          </div>
        </div>
      </div>

      {items.length === 0 ? (
        <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg p-8 text-center">
          <p className="text-gray-500 dark:text-gray-400">
            {filters.status === "PENDING" ? "No pending review items." : "No review items found."}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-[#131421] border border-gray-200 dark:border-[rgba(255,255,255,0.1)] shadow-sm dark:shadow-none rounded-lg">
          <DataTable rows={items} columns={columns} rowKey={(item) => item.id} />
        </div>
      )}

      {totalPages > 1 && (
        <TablePagination page={filters.page} totalPages={totalPages} onJump={(p) => setFilters((f) => ({ ...f, page: p }))} />
      )}

      <ModalNotesAction
        data={resolutionModalId}
        handler={() => setResolutionModalId(null)}
        text="Mark as Reviewed"
        actionText="Confirm"
        actionHandler={handleConfirmResolve}
        placeholder="e.g., Investigated and confirmed false positive..."
        loading={resolvingId !== null}
      />
    </div>
  )
}
